import { Bug, Home, BedDouble, Rat, Wind, CircleDot, Zap, Building2, ArrowUpRight } from "lucide-react";
import { ChapterTag, MaskedLines, FadeUp } from "./Reveal";
import { BookButton } from "./CTA";
import { useLead } from "../context/LeadContext";

const SERVICES = [
  { icon: Bug, pest: "Cockroaches", name: "Cockroach Control", text: "Gel baiting and targeted treatment of kitchens, drains and harbourage points.", solves: "Recurring roach activity" },
  { icon: Home, pest: "Termites", name: "Termite Control", text: "Pre- and post-construction termite management that protects the structure itself.", solves: "Silent structural damage" },
  { icon: BedDouble, pest: "Bed Bugs", name: "Bed Bug Control", text: "Systematic treatment of beds, furniture and crevices with planned follow-up.", solves: "Bites and sleepless nights" },
  { icon: Rat, pest: "Rodents / Rats", name: "Rodent Control", text: "Trapping, baiting and proofing of entry points for lasting rodent management.", solves: "Contamination and gnawing damage" },
  { icon: Wind, pest: "Mosquitoes", name: "Mosquito Control", text: "Breeding-site reduction plus residual and misting treatment where required.", solves: "Bites and breeding sites" },
  { icon: CircleDot, pest: "Ants", name: "Ant Control", text: "Colony-focused baiting that eliminates the source, not just the trail.", solves: "Trails in kitchens and pantries" },
  { icon: Zap, pest: "Flies", name: "Fly Control", text: "Source identification, exclusion advice and professional fly management.", solves: "Hygiene risk around food areas" },
  { icon: Building2, pest: "Commercial Property", name: "Commercial Pest Management", text: "Scheduled, documented pest-management programs for offices, restaurants and facilities.", solves: "Compliance and reputation" },
];

export const Services = () => {
  const { openModal } = useLead();
  return (
    <section id="services" data-testid="services-section" className="py-24 lg:py-32 scroll-mt-20">
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <ChapterTag index="03" label="Services" />
        <div className="mt-10 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <h2 data-testid="services-headline" className="font-display font-semibold text-charcoal tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl max-w-2xl">
            <MaskedLines lines={[<>Professional pest control for</>, <span className="italic text-wine">homes & businesses.</span>]} />
          </h2>
          <FadeUp delay={0.25}>
            <p className="text-sm text-stone2 max-w-xs leading-relaxed">Every treatment begins with a proper inspection. The final service list is confirmed with you before any work begins.</p>
          </FadeUp>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border mt-14">
          {SERVICES.map((s, i) => (
            <FadeUp key={s.name} delay={(i % 4) * 0.07} className="bg-ivory p-8 flex flex-col group hover:bg-cream transition-colors duration-500" >
              <div className="h-11 w-11 border border-wine/25 flex items-center justify-center mb-6 group-hover:bg-wine transition-colors duration-500">
                <s.icon className="h-5 w-5 text-wine group-hover:text-ivory transition-colors duration-500" strokeWidth={1.5} />
              </div>
              <h3 data-testid={`service-${s.name.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="font-display text-[22px] font-semibold text-charcoal leading-snug">{s.name}</h3>
              <p className="text-sm text-stone2 leading-relaxed mt-2.5 flex-1">{s.text}</p>
              <p className="text-[11px] tracking-[0.16em] uppercase font-bold text-gold mt-5">Solves: {s.solves}</p>
              <button
                data-testid={`service-cta-${s.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => openModal(s.pest)}
                className="mt-4 inline-flex items-center gap-1.5 text-[12px] font-bold tracking-[0.12em] uppercase text-wine hover:gap-3 transition-all duration-300"
              >
                Get Inspection <ArrowUpRight className="h-3.5 w-3.5" />
              </button>
            </FadeUp>
          ))}
        </div>

        <FadeUp delay={0.2} className="mt-12">
          <BookButton testid="services-book-btn" />
        </FadeUp>
      </div>
    </section>
  );
};
